import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, CheckCircle, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing token"); setLoading(false); return; }
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/auth/invite-info/${token}`);
        if (active) setInfo(r.data);
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || "Invite invalid");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const accept = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/api/auth/accept-invite`, { token, password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to accept invite");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-pulse text-slate-500">Loading invite…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full" data-testid="accept-invite-card">
        <CardContent className="p-8">
          <div className="flex justify-center mb-4">
            <div className="bg-slate-900 p-3 rounded-full">
              <Shield className="h-6 w-6 text-white" />
            </div>
          </div>
          {success ? (
            <div className="text-center" data-testid="accept-invite-success">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-2">Welcome aboard!</h2>
              <p className="text-slate-600 text-sm">Redirecting you to login…</p>
            </div>
          ) : error && !info ? (
            <div className="text-center" data-testid="accept-invite-error">
              <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-2">Invite invalid</h2>
              <p className="text-slate-600 text-sm">{error}</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-heading font-bold text-center mb-2">
                Accept invitation
              </h2>
              <p className="text-center text-slate-600 text-sm mb-6">
                <strong>{info?.organisationName || "An organisation"}</strong> has invited you to join
                as <strong>{(info?.role || "fundraiser").replace("_", " ")}</strong>.
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Email</Label>
                  <Input value={info?.email || ""} disabled data-testid="invite-email" />
                </div>
                <div>
                  <Label htmlFor="pw">Set password</Label>
                  <Input id="pw" type="password" value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid="invite-password" />
                </div>
                <div>
                  <Label htmlFor="cpw">Confirm password</Label>
                  <Input id="cpw" type="password" value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    data-testid="invite-password-confirm" />
                </div>
                {error && (
                  <div className="text-sm text-rose-600" data-testid="invite-error">{error}</div>
                )}
                <Button onClick={accept} disabled={submitting}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  data-testid="invite-accept-btn">
                  {submitting ? "Activating…" : "Activate account"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
